import React, { useState, useRef, useEffect, useDeferredValue } from 'react';
import { Tag } from '@/components/Tag';
import Checkbox from '@/components/Checkbox';
import { MdCancel, MdSearch } from 'react-icons/md';

// オプションの型定義
export interface MultiSelectAutocompleteOption {
  id: number | string;
  name: string;
}

interface MultiSelectAutocompleteProps {
  options: MultiSelectAutocompleteOption[];
  value: (number | string)[];
  onChange: (value: (number | string)[]) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  error?: string;
  className?: string;
}

export function MultiSelectAutocomplete({
  options,
  value,
  onChange,
  placeholder = 'テキスト',
  disabled = false,
  required = false,
  label,
  error,
  className = '',
}: MultiSelectAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // 選択されているオプションを取得
  const selectedOptions = options.filter((option) => value.includes(option.id));

  // 検索フィルタリング
  const filteredOptions = options.filter((option) =>
    option.name.toLowerCase().includes(deferredQuery.toLowerCase())
  );

  // チェックボックスの変更ハンドラ
  const handleToggleOption = (optionId: number | string) => {
    const isSelected = value.includes(optionId);
    if (isSelected) {
      onChange(value.filter((id) => id !== optionId));
    } else {
      onChange([...value, optionId]);
    }
    // 選択後に入力フィールドをクリアしてフォーカスを戻す
    setTimeout(() => {
      setQuery('');
      setHighlightedIndex(0);
      inputRef.current?.focus({ preventScroll: true });
    }, 0);
  };

  // 値を削除
  const removeValue = (optionId: number | string) => {
    onChange(value.filter((id) => id !== optionId));
  };

  // 外側のクリックを検知してドロップダウンを閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // キーボード操作
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => Math.min(prev + 1, filteredOptions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (isOpen && filteredOptions[highlightedIndex]) {
          const option = filteredOptions[highlightedIndex];
          handleToggleOption(option.id);
        } else {
          setIsOpen(true);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  // ハイライトされた項目をスクロール表示
  useEffect(() => {
    if (isOpen && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  return (
    <div className={`w-full ${className}`}>
      {/* ラベル */}
      {label && (
        <div className="mb-2 flex items-center gap-2">
          <span className="text-heading-xxs">{label}</span>
          {required && (
            <Tag color="red" size="sm">
              必須
            </Tag>
          )}
        </div>
      )}

      {/* 入力フィールド */}
      <div className="relative" ref={containerRef}>
        <div
          className={`hidden-scrollbar flex min-h-[42px] w-full items-center gap-1 overflow-x-auto rounded border border-solid bg-white px-3 py-2 transition-all ${
            isOpen
              ? 'border-primary outline outline-2 outline-offset-[-2px] outline-primary'
              : error
                ? 'border-danger bg-danger_lighten!'
                : 'border-border'
          } ${disabled ? 'cursor-not-allowed bg-gray!' : ''}`}
        >
          {/* 検索アイコン */}
          <MdSearch className="shrink-0 text-xl text-sub" />

          {/* 選択されたタグ */}
          {selectedOptions.map((option) => (
            <Tag key={option.id} color="default" className="flex shrink-0 items-center gap-1">
              {option.name}
              {!disabled && (
                <button
                  type="button"
                  className="flex size-4 items-center justify-center rounded-full border-none bg-transparent p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeValue(option.id);
                  }}
                >
                  <MdCancel className="fill-main text-base opacity-50" />
                </button>
              )}
            </Tag>
          ))}

          {/* 入力フィールド */}
          <input
            ref={inputRef}
            type="text"
            className="min-w-[100px] flex-1 border-none bg-transparent text-body-sm text-main outline-hidden placeholder:text-placeholder disabled:cursor-not-allowed"
            placeholder={selectedOptions.length === 0 ? placeholder : ''}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => !disabled && setIsOpen(true)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
          />
        </div>

        {/* ドロップダウンリスト */}
        {isOpen && !disabled && (
          <div
            ref={listRef}
            className="absolute z-[9999] mt-1 max-h-[264px] w-full overflow-y-auto rounded border border-solid border-border bg-white shadow-layer3"
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option.id}
                  className={`flex cursor-pointer items-center gap-2 px-3 py-2 transition-colors ${
                    index === highlightedIndex ? 'bg-layer' : 'hover:bg-layer'
                  }`}
                  onClick={(e) => {
                    if (e.target === e.currentTarget) handleToggleOption(option.id);
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <Checkbox
                    checked={value.includes(option.id)}
                    onChange={() => handleToggleOption(option.id)}
                  >
                    <span className="font-normal">{option.name}</span>
                  </Checkbox>
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-body-sm text-placeholder">
                該当する項目がありません
              </div>
            )}
          </div>
        )}
      </div>

      {/* エラーメッセージ */}
      {error && <p className="mt-1 text-body-xs text-danger">＊{error}</p>}
    </div>
  );
}

export default MultiSelectAutocomplete;
